import { InstrumentId, Timeframe } from "@trader/shared/MarketData"
import { Effect, Option, Schema } from "effect"
import { IndicatorStyle } from "./indicatorStyleSchema.ts"
import { LAYOUT_IDS, layoutById, MAX_PANES } from "./layouts.ts"

// The workspace document: its schema and pure transforms (no storage, no React),
// so they run and are tested anywhere. workspaceStore.ts holds the live state.
//
// Indicators are shared by every chart on screen, whatever symbol each shows
// (each chart computes them on its own bars). The shared list is stored on every
// pane, identical everywhere: a build from before sharing (a rollback, a stale
// tab) still reads each chart's indicators. It drops their styles when it saves
// (its schema has none), so they're back to the defaults after a rollback.

export const IndicatorInstance = Schema.Struct({
  /** Unique per added indicator (the same indicator can be added twice). */
  uid: Schema.String,
  /** Catalogue id, e.g. "rsi". */
  id: Schema.String,
  /** Inputs that differ from the indicator's defaults. */
  inputs: Schema.Record(Schema.String, Schema.Unknown),
  hidden: Schema.Boolean,
  /** Style that differs from the indicator's defaults. One that doesn't decode is dropped, not the workspace. */
  style: Schema.optional(IndicatorStyle.pipe(Schema.catchDecoding(() => Effect.succeed(Option.none()))))
})
export type IndicatorInstance = typeof IndicatorInstance.Type
export type IndicatorPatch = Partial<Omit<IndicatorInstance, "uid" | "id">>

export const StrategyInstance = Schema.Struct({
  /** Catalogue id, e.g. "builtin:ma-cross". */
  id: Schema.String,
  /** Inputs and properties that differ from the strategy's own. */
  inputs: Schema.Record(Schema.String, Schema.Unknown),
  properties: Schema.Record(Schema.String, Schema.Unknown),
  /** Deep backtest: run on all stored history, not just the bars loaded in the chart. */
  deep: Schema.Boolean,
  hidden: Schema.Boolean,
  /** Backtest from this bar time (epoch seconds) on, not the first bar: e.g. the first day FX rates cover. */
  from: Schema.optional(Schema.Int),
  /** Backtest the bars opening before this time (epoch seconds) only: the Backtesting range's end. */
  to: Schema.optional(Schema.Int)
})
export type StrategyInstance = typeof StrategyInstance.Type

export const PaneState = Schema.Struct({
  instrumentId: InstrumentId,
  timeframe: Timeframe,
  // Absent in workspaces saved before indicators / strategies existed.
  indicators: Schema.optional(Schema.Array(IndicatorInstance)),
  strategy: Schema.optional(Schema.NullOr(StrategyInstance))
})
export type PaneState = typeof PaneState.Type

export const WorkspaceState = Schema.Struct({
  layout: Schema.Literals(LAYOUT_IDS),
  panes: Schema.Array(PaneState),
  active: Schema.Int
})
export type WorkspaceState = typeof WorkspaceState.Type

export const DEFAULT_WORKSPACE: WorkspaceState = {
  layout: "1",
  active: 0,
  panes: [
    { instrumentId: InstrumentId.make("NSE:RELIANCE"), timeframe: "5m" },
    { instrumentId: InstrumentId.make("NSE:TCS"), timeframe: "15m" },
    { instrumentId: InstrumentId.make("NSE:HDFCBANK"), timeframe: "1h" },
    { instrumentId: InstrumentId.make("NSE:INFY"), timeframe: "1D" }
  ]
}

export const paneIndicators = (pane: PaneState | undefined): ReadonlyArray<IndicatorInstance> => pane?.indicators ?? []

/**
 * The active pane is always one the layout shows: the toolbar, the Indicators
 * dialog, and the Strategy Tester act on it, so it must never be a hidden pane.
 */
/** The instruments of the panes the layout shows, each once: what a replay follows (docs/design/0011). */
export const shownInstruments = (workspace: WorkspaceState): Array<InstrumentId> => [
  ...new Set(workspace.panes.slice(0, layoutById(workspace.layout).panes).map((pane) => pane.instrumentId))
]

export const withActiveShown = (workspace: WorkspaceState): WorkspaceState => {
  const active = Math.max(0, Math.min(workspace.active, layoutById(workspace.layout).panes - 1))
  return active === workspace.active ? workspace : { ...workspace, active }
}

/** The indicators every chart shows. */
export const sharedIndicators = (workspace: WorkspaceState): ReadonlyArray<IndicatorInstance> =>
  paneIndicators(workspace.panes[workspace.active])

const canonical = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(canonical)
    : value !== null && typeof value === "object"
    ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical((value as Record<string, unknown>)[key])]))
    : value

/** What an indicator computes and looks like (not its uid or whether it's hidden). */
export const indicatorSettingsKey = ({ hidden: _hidden, uid: _uid, ...settings }: IndicatorInstance): string =>
  JSON.stringify(canonical(settings))

/**
 * One list from several charts' lists, in the order given: each indicator (by
 * settings) as many times as the chart that has it most, so identical copies
 * across charts become one and EMA ×2 on one chart stays ×2. A uid already
 * taken gets a new one.
 */
export const mergeIndicatorSets = (
  sets: ReadonlyArray<ReadonlyArray<IndicatorInstance>>,
  newUid: () => string
): ReadonlyArray<IndicatorInstance> => {
  const merged: Array<IndicatorInstance> = []
  const taken = new Map<string, number>()
  const uids = new Set<string>()
  for (const set of sets) {
    const seen = new Map<string, number>()
    for (const instance of set) {
      const key = indicatorSettingsKey(instance)
      const count = (seen.get(key) ?? 0) + 1
      seen.set(key, count)
      if (count <= (taken.get(key) ?? 0)) continue
      taken.set(key, count)
      const uid = uids.has(instance.uid) ? newUid() : instance.uid
      uids.add(uid)
      merged.push(uid === instance.uid ? instance : { ...instance, uid })
    }
  }
  return merged
}

const sameList = (a: ReadonlyArray<IndicatorInstance>, b: ReadonlyArray<IndicatorInstance>): boolean =>
  a.length === b.length &&
  a.every((instance, i) =>
    instance.uid === b[i]!.uid && instance.hidden === b[i]!.hidden && indicatorSettingsKey(instance) === indicatorSettingsKey(b[i]!)
  )

/**
 * Makes every pane carry the same list: the charts on screen merged, the
 * active one first (a workspace saved before sharing had a list per chart).
 * Lists of panes the layout hides are replaced. Already shared: unchanged.
 */
export const withSharedIndicators = (workspace: WorkspaceState, newUid: () => string): WorkspaceState => {
  const shown = layoutById(workspace.layout).panes
  const order = [workspace.active, ...Array.from({ length: shown }, (_, i) => i).filter((i) => i !== workspace.active)]
  const merged = mergeIndicatorSets(order.map((i) => paneIndicators(workspace.panes[i])), newUid)
  if (workspace.panes.every((pane) => sameList(paneIndicators(pane), merged))) return workspace
  return { ...workspace, panes: workspace.panes.map((pane) => ({ ...pane, indicators: merged })) }
}

/** Changes the shared list, on every pane. */
export const editIndicators = (
  workspace: WorkspaceState,
  change: (list: ReadonlyArray<IndicatorInstance>) => ReadonlyArray<IndicatorInstance>
): WorkspaceState => {
  const list = change(sharedIndicators(workspace))
  return { ...workspace, panes: workspace.panes.map((pane) => ({ ...pane, indicators: list })) }
}

const decode = Schema.decodeUnknownOption(Schema.fromJsonString(WorkspaceState))

/** A saved workspace (localStorage or the server's copy), normalised; null when it isn't one. */
export const decodeWorkspace = (json: string | null, newUid: () => string): WorkspaceState | null => {
  const saved = decode(json ?? "")
  if (saved._tag === "None" || saved.value.panes.length !== MAX_PANES) return null
  return withSharedIndicators(withActiveShown(saved.value), newUid)
}

